import { Controller } from "@hotwired/stimulus"

// Autosave controller for draft entries
export default class extends Controller {
  static values = {
    url: String,
    delay: { type: Number, default: 3000 },
    entryId: String,
    textSaving: { type: String, default: "Saving..." },
    textSaved: { type: String, default: "Saved" },
    textUnsaved: { type: String, default: "Unsaved" },
    textFailed: { type: String, default: "Save failed" },
    textDraftFound: { type: String, default: "Unsaved draft found." },
    textRestore: { type: String, default: "Restore" },
    textDismiss: { type: String, default: "Dismiss" }
  }
  static targets = ["dot", "status", "retry"]

  connect() {
    this.pendingSave = false
    this.retryCount = 0
    this.maxRetries = 2
    this.lastSavedData = this.getFormData()

    // Store bound handlers for proper cleanup
    this.boundScheduleAutosave = this.scheduleAutosave.bind(this)
    this.boundSaveImmediately = this.saveImmediately.bind(this)

    // Listen for input changes
    this.element.addEventListener("input", this.boundScheduleAutosave)

    // Save on page unload
    window.addEventListener("beforeunload", this.boundSaveImmediately)

    // Check for recovered draft
    this.checkLocalDraft()
  }

  disconnect() {
    this.clearTimeout()
    this.clearRetryTimeout()
    this.element.removeEventListener("input", this.boundScheduleAutosave)
    window.removeEventListener("beforeunload", this.boundSaveImmediately)
  }

  // Handle keyboard shortcuts: ⌘S to save, ⌘Enter to publish
  handleKeydown(event) {
    if (!event.metaKey && !event.ctrlKey) return

    if (event.key === "s") {
      event.preventDefault()
      this.save()
    }

    if (event.key === "Enter") {
      event.preventDefault()
      // Find the publish button and click it
      const publishBtn = this.element.querySelector('[name="publish"]')
      if (publishBtn) {
        publishBtn.click()
      } else {
        // If already published, just save
        this.element.requestSubmit()
      }
    }
  }

  scheduleAutosave() {
    // Only autosave if we have a URL (existing entry)
    if (!this.urlValue) {
      this.showUnsaved()
      return
    }

    this.showUnsaved()
    this.clearTimeout()
    this.saveTimeout = setTimeout(() => this.save(), this.delayValue)
  }

  clearTimeout() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
      this.saveTimeout = null
    }
  }

  clearRetryTimeout() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout)
      this.retryTimeout = null
    }
  }

  async save() {
    if (this.pendingSave) return
    if (!this.urlValue) return

    const currentData = this.getFormData()
    if (this.dataEquals(currentData, this.lastSavedData)) return

    this.pendingSave = true
    this.showSaving()
    this.hideRetry()

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        body: currentData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        }
      })

      if (response.ok) {
        this.lastSavedData = currentData
        this.retryCount = 0
        this.showSaved()
        this.clearLocalDraft()
      } else {
        this.handleSaveFailure(currentData)
      }
    } catch (error) {
      console.error("Autosave failed:", error)
      this.handleSaveFailure(currentData)
    } finally {
      this.pendingSave = false
    }
  }

  handleSaveFailure(formData) {
    // Save to localStorage as fallback
    this.saveLocalDraft(formData)

    if (this.retryCount < this.maxRetries) {
      this.retryCount++
      const delay = Math.pow(2, this.retryCount) * 1000 // 2s, 4s
      this.showError()
      this.retryTimeout = setTimeout(() => this.save(), delay)
    } else {
      this.retryCount = 0
      this.showError()
      this.showRetry()
    }
  }

  retry() {
    this.retryCount = 0
    this.hideRetry()
    this.save()
  }

  saveImmediately() {
    this.clearTimeout()
    if (this.urlValue && !this.pendingSave) {
      const currentData = this.getFormData()
      if (this.dataEquals(currentData, this.lastSavedData)) return

      // Use fetch with keepalive for PATCH request on page unload
      fetch(this.urlValue, {
        method: "PATCH",
        body: currentData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        keepalive: true
      })
    }
  }

  // --- localStorage draft recovery ---

  get storageKey() {
    return `opentil_draft_${this.entryIdValue || "new"}`
  }

  saveLocalDraft(formData) {
    if (!this.entryIdValue) return

    try {
      const data = {}
      for (const field of this.fieldsToCompare) {
        data[field] = formData.get(field) || ""
      }
      data._savedAt = Date.now()
      localStorage.setItem(this.storageKey, JSON.stringify(data))
    } catch {
      // localStorage might be full or disabled
    }
  }

  clearLocalDraft() {
    try {
      localStorage.removeItem(this.storageKey)
    } catch {
      // Ignore
    }
  }

  checkLocalDraft() {
    if (!this.entryIdValue) return

    try {
      const raw = localStorage.getItem(this.storageKey)
      if (!raw) return

      const draft = JSON.parse(raw)
      if (!draft._savedAt) return

      // Only recover if draft is less than 24 hours old
      const age = Date.now() - draft._savedAt
      if (age > 24 * 60 * 60 * 1000) {
        this.clearLocalDraft()
        return
      }

      // Check if draft differs from current form
      const currentData = this.getFormData()
      let isDifferent = false
      for (const field of this.fieldsToCompare) {
        if ((draft[field] || "") !== (currentData.get(field) || "")) {
          isDifferent = true
          break
        }
      }

      if (isDifferent) {
        this.showDraftRecovery(draft)
      } else {
        this.clearLocalDraft()
      }
    } catch {
      // Ignore parse errors
    }
  }

  showDraftRecovery(draft) {
    if (this.hasStatusTarget) {
      this.statusTarget.innerHTML =
        `<span class="draft-recovery">` +
        `${this.escapeText(this.textDraftFoundValue)} ` +
        `<button type="button" class="btn-link" data-action="autosave#restoreDraft">${this.escapeText(this.textRestoreValue)}</button> · ` +
        `<button type="button" class="btn-link" data-action="autosave#dismissDraft">${this.escapeText(this.textDismissValue)}</button>` +
        `</span>`
    }
    this._pendingDraft = draft
  }

  restoreDraft() {
    const draft = this._pendingDraft
    if (!draft) return

    for (const field of this.fieldsToCompare) {
      if (draft[field] !== undefined) {
        const input = this.element.querySelector(`[name="${field}"]`)
        if (input) {
          input.value = draft[field]
          input.dispatchEvent(new Event("input", { bubbles: true }))
        }
      }
    }

    this._pendingDraft = null
    this.clearLocalDraft()
    this.showUnsaved()
  }

  dismissDraft() {
    this._pendingDraft = null
    this.clearLocalDraft()
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.textSavedValue
    }
  }

  // --- UI helpers ---

  getFormData() {
    return new FormData(this.element)
  }

  get fieldsToCompare() {
    return ["entry[title]", "entry[content]", "entry[category_id]", "entry[tag_names]", "entry[slug]", "entry[summary]", "entry[meta_description]", "entry[lang]", "entry[visibility]"]
  }

  dataEquals(a, b) {
    for (const field of this.fieldsToCompare) {
      if (a.get(field) !== b.get(field)) return false
    }
    return true
  }

  showSaving() {
    if (this.hasDotTarget) {
      this.dotTarget.className = "save-indicator-dot saving"
    }
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.textSavingValue
    }
  }

  showSaved() {
    if (this.hasDotTarget) {
      this.dotTarget.className = "save-indicator-dot"
    }
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.textSavedValue
    }
  }

  showUnsaved() {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.textUnsavedValue
    }
  }

  showError() {
    if (this.hasDotTarget) {
      this.dotTarget.className = "save-indicator-dot error"
    }
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = this.textFailedValue
    }
  }

  showRetry() {
    if (this.hasRetryTarget) {
      this.retryTarget.style.display = ""
    }
  }

  hideRetry() {
    if (this.hasRetryTarget) {
      this.retryTarget.style.display = "none"
    }
  }

  escapeText(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
